import { DateTime } from "luxon";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Col, Nav, Row, Tab } from "react-bootstrap";
import EventAgendaItem from "./EventAgendaItem";

import { ApplicationContext } from "../../contexts/ApplicationContext";
import "./EventAgenda.scss";
import { Session } from "../../services/EventService";

export type EventKey = string | number;

function AgendaDateNav({ dates, className, onTabClick }: { dates: string[]; className?: string; onTabClick?: () => void }) {
    return (
        <Nav variant="underline" className={className} role="tablist">
            {dates.map((day) => (
                <Nav.Item key={day}>
                    <Nav.Link eventKey={day} onClick={onTabClick}>{day}</Nav.Link>
                </Nav.Item>
            ))}
        </Nav>
    );
}

function groupSessionsByDays(sessions: any) {
    // Sort by date
    sessions
        .sort((a: any, b: any) => new Date(a.start).getTime() - new Date(b.start).getTime());

    // Group by day
    const sessionsByDay = new Map<string, any[]>();
    sessions.forEach((session: any) => {
        const _startDay = DateTime.fromISO(session.start).toFormat('dd.LL.yyyy');
        const _sessions = sessionsByDay.get(_startDay) ?? [];
        _sessions.push(session);
        sessionsByDay.set(_startDay, _sessions);
    });

    // Group day sessions by session start
    sessionsByDay.forEach((sessions, day) => {
        const groupedEvents = sessions.reduce((acc, session) => {
            // If the group with this startTime doesn't exist, create it
            if (!acc[session.start]) {
                acc[session.start] = [];
            }
            // Add the session to the corresponding group
            acc[session.start].push(session);
            return acc;
        }, {});

        // Sort session group by location
        for (const eventGroupArr of Object.values(groupedEvents) as any[]) {
            eventGroupArr.sort((a: Session, b: Session) => a.location?.localeCompare(b.location ?? ''));
        }

        sessionsByDay.set(day, Object.values(groupedEvents));
    });

    return sessionsByDay;
}

function EventAgendaView() {
    const maxSessionInLine = 3;
    const { eventItem } = useContext(ApplicationContext);

    const [sessions, setSessions] = useState<Map<string, any[]>>(new Map());

    const [activeTab, setActiveTab] = useState<string>("");
    const [uniqueDates, setUniqueDates] = useState<string[]>([]);
    const topDateNavRef = useRef<HTMLDivElement>(null);

    const scrollToTopDateNav = useCallback(() => {
        const el = topDateNavRef.current;
        if (!el) return;

        const header = document.querySelector('.wp-site-blocks > header');
        const topOffset = header ? header.getBoundingClientRect().height : 0;
        const targetScrollY = window.scrollY + el.getBoundingClientRect().top - topOffset;

        window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
    }, []);

    const setupView = () => {
        if (eventItem?.sessions) {
            const _sessions = groupSessionsByDays(eventItem.sessions);
            const dates = Array.from(_sessions.keys());

            setSessions(_sessions);
            setUniqueDates(dates);
            setActiveTab(dates[0] ?? "");
        }
    };

    useEffect(() => {
        setupView();
    }, [eventItem]);

    const handleSelect = (key: string | null) => {
        if (key) setActiveTab(key);
    };

    return (
        <Tab.Container activeKey={activeTab} onSelect={handleSelect}>
            <div ref={topDateNavRef}>
                <AgendaDateNav dates={uniqueDates} className="event__agenda-date-nav event__agenda-date-nav--top mb-4" />
            </div>
            <Tab.Content>
                {Array.from(sessions.entries()).map(([day, _sessionGroups]) => (
                    <Tab.Pane eventKey={day} key={day} className="event__agenda-list">
                        {_sessionGroups.map((_sessionGroup, _sessionGroupIndex) => (
                            <Row key={_sessionGroupIndex} className="d-flex align-items-stretch event__agenda-list-row">
                                {_sessionGroup.length > maxSessionInLine && <h3>{_sessionGroup[0].startTime}</h3>}
                                {_sessionGroup.map((session: any, sessionIndex: number) => {
                                    const colorClass = `event__agenda-item-${sessionIndex + 1}`;
                                    return <Col className="mb-2" key={sessionIndex}><EventAgendaItem className={`${colorClass}`} session={session} /></Col>
                                })}
                            </Row>
                        ))}
                    </Tab.Pane>
                ))}
            </Tab.Content>
            <AgendaDateNav dates={uniqueDates} className="event__agenda-date-nav event__agenda-date-nav--bottom mt-4" onTabClick={scrollToTopDateNav} />
        </Tab.Container>
    );
}

export default EventAgendaView;
